import { PropertyFilterProps, TableProps } from '@amzn/awsui-components-core';
import LocalStorage from '../../common/LocalStorage';
import { v4 as uuid } from 'uuid';
import { localLog } from '../../loggers/Logger';
import {
  WorkflowSteps,
  WorkflowStepsCurrentStateLabel,
} from '@amzn/awscat-pattern-library-service-model';
import { APGLEntityEntry } from '@amzn/awscat-editor/dist/extensions/apgl-entity';
import { convertEntities } from '@amzn/awscat-editor';

export declare type Operator = '<' | '<=' | '>' | '>=' | ':' | '!:' | '=' | '!=';

export interface FilterProperty {
  key: string;
  groupValuesLabel: string;
  propertyLabel: string;
  operators?: readonly Operator[];
  defaultOperator?: Operator;
  group?: string;
}

export enum ColumnTypes {
  TEXT = 'text',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  CUSTOM = 'custom',
}

export interface ColumnDefinition<I> extends TableProps.ColumnDefinition<I> {
  type: ColumnTypes;
}

export const booleanOperators: Operator[] = ['='];
export const nonNumericOperators: Operator[] = [':', '!:', '=', '!='];
export const numericOperators: Operator[] = ['<', '<=', '>', '>=', '=', '!='];

export const DefaultI18nStrings: PropertyFilterProps.I18nStrings = {
  filteringAriaLabel: 'your choice',
  dismissAriaLabel: 'Dismiss',
  filteringPlaceholder: 'Search',
  groupValuesText: 'Values',
  groupPropertiesText: 'Properties',
  operatorsText: 'Operators',
  operationAndText: 'and',
  operationOrText: 'or',
  operatorLessText: 'Less than',
  operatorLessOrEqualText: 'Less than or equal',
  operatorGreaterText: 'Greater than',
  operatorGreaterOrEqualText: 'Greater than or equal',
  operatorContainsText: 'Contains',
  operatorDoesNotContainText: 'Does not contain',
  operatorEqualsText: 'Equals',
  operatorDoesNotEqualText: 'Does not equal',
  editTokenHeader: 'Edit filter',
  propertyText: 'Property',
  operatorText: 'Operator',
  valueText: 'Value',
  cancelActionText: 'Cancel',
  applyActionText: 'Apply',
  allPropertiesLabel: 'All properties',
  tokenLimitShowMore: 'Show more',
  tokenLimitShowFewer: 'Show fewer',
  clearFiltersText: 'Clear',
  removeTokenButtonAriaLabel: () => 'Remove token',
  enteredTextLabel: (text) => `Use: "${text}"`,
};

export const DefaultCustomGroupsText: PropertyFilterProps.GroupText[] = [
  {
    properties: 'Tags',
    values: 'Tag values',
    group: 'tags',
  },
];

// @ts-expect-error TS7006 - strict migration
export const getFilterCounterText = (count) => `${count} ${count === 1 ? 'match' : 'matches'}`;

const isNumericColumn = (type: ColumnTypes) => type === ColumnTypes.NUMBER;
const isBooleanColumn = (type: ColumnTypes) => type === ColumnTypes.BOOLEAN;

const nonApplicableFilters = [
  'author',
  'contentId',
  'contentName',
  'contentInfo',
  'notes',
  'techWriterQueue',
  'lastPublicationDate',
  'lastApgPublicationDate',
  'lastApgPublicationVersion',
  'approvalsCount',
  'disapprovalsCount',
  'engagements',
  'likes',
  'rank',
];

export const buildTableFilterProperties = (columnDefinitions: any[]): FilterProperty[] => {
  const filterProperties: FilterProperty[] = columnDefinitions
    .filter((columnDef) => !nonApplicableFilters.includes(columnDef.id))
    .map((columnDef) => {
      const operators = isBooleanColumn(columnDef.type)
        ? booleanOperators
        : isNumericColumn(columnDef.type)
          ? numericOperators
          : nonNumericOperators;
      return {
        propertyLabel: `${columnDef.filterLabel}` || columnDef.text || columnDef.id,
        key: columnDef.id,
        groupValuesLabel: `${columnDef.filterLabel} values`,
        operators,
        group: columnDef.group || '',
      };
    });
  return filterProperties;
};

export const getDefaultTechwriterQuery = (currentTechWriter: string) => {
  return {
    tokens: [
      { propertyKey: 'techWriterPool', operator: '=', value: 'true' },
      { propertyKey: 'techWriter', operator: '=', value: currentTechWriter },
    ],
    operation: 'and',
  };
};

export const getDefaultBarRaiserQuery = () => {
  return {
    tokens: [
      {
        propertyKey: 'progress',
        operator: '=',
        value: WorkflowStepsCurrentStateLabel.get(WorkflowSteps.Publisher),
      },
    ],
    operation: 'or',
  };
};

// @ts-expect-error TS7006 - strict migration
export const getDefaultFilterKey = (tableId) => `${tableId}:Default`;
// @ts-expect-error TS7006 - strict migration
export const getCustomFilterListKey = (tableId) => `${tableId}:CustomFilterList`;

export const getDefaultCachedQuery = (tableId: string, defaultQuery: any) => {
  try {
    const defaultFilterKeyValue = LocalStorage.getTableQueryFilter(getDefaultFilterKey(tableId));
    if (defaultFilterKeyValue && typeof defaultFilterKeyValue === 'string') {
      const cachedQuery = LocalStorage.getTableQueryFilter(`${tableId}:${defaultFilterKeyValue}`);
      if (cachedQuery && typeof cachedQuery === 'string') {
        return JSON.parse(cachedQuery);
      }
    }
  } catch (error) {
    localLog.warn({ error }, `Error while getting default cached query for ${tableId}`);
  }
  return defaultQuery;
};

export const setDefaultCachedQuery = (tableId: string, defaultQuery: any) => {
  if (defaultQuery) {
    const defaultFilterKey = getDefaultFilterKey(tableId);
    let defaultFilterKeyValue = LocalStorage.getTableQueryFilter(defaultFilterKey);
    if (!defaultFilterKeyValue || typeof defaultFilterKeyValue !== 'string') {
      defaultFilterKeyValue = uuid();
      LocalStorage.setTableQueryFilter(defaultFilterKey, defaultFilterKeyValue);
    }
    LocalStorage.setTableQueryFilter(
      `${tableId}:${defaultFilterKeyValue}`,
      JSON.stringify(defaultQuery),
    );
  }
};

// Custom property filtering logic
export const freeTextProperties = ['author', 'contentId', 'contentName'];

export const buildTableFilterPropertiesMap = (filteringProperties: FilterProperty[]) => {
  return filteringProperties.reduce(
    // @ts-expect-error TS2345 - strict migration
    (map, obj) => map.set(obj.key, obj.operators),
    new Map<string, readonly Operator[]>(),
  );
};

export const applyCustomPropertyFilter = (
  item: any,
  query: any,
  filteringPropertiesMap: Map<string, readonly Operator[]>,
  textProperties: string[],
  entities: { [key: string]: APGLEntityEntry } = {},
) => {
  const tokens = query.tokens;
  const operation = query.operation;
  let result = operation === 'and' ? true : !tokens.length;
  // @ts-expect-error TS7006 - strict migration
  tokens.forEach((token) => {
    // @ts-expect-error TS2322 - strict migration
    result =
      operation === 'and'
        ? result && filterByToken(token, item, filteringPropertiesMap, textProperties, entities)
        : result || filterByToken(token, item, filteringPropertiesMap, textProperties, entities);
    return result;
  });
  return result;
};

// @ts-expect-error TS7030 - strict migration
const filterUsingOperator = (
  itemValue: string,
  tokenValue: string,
  operator: string,
  entities: { [key: string]: APGLEntityEntry } = {},
) => {
  switch (operator) {
    case '<':
      return itemValue < tokenValue;
    case '<=':
      return itemValue <= tokenValue;
    case '>':
      return itemValue > tokenValue;
    case '>=':
      return itemValue >= tokenValue;
    case '=':
      return itemValue === tokenValue;
    case '!=':
      return itemValue !== tokenValue;
    case ':':
      return (
        convertEntities(itemValue + '', entities)
          .toLowerCase()
          .indexOf((tokenValue + '').toLowerCase()) > -1
      );
    case '!:':
      return (itemValue + '').toLowerCase().indexOf((tokenValue + '').toLowerCase()) === -1;
  }
};

const freeTextFilter = (
  value: string,
  item: any,
  textProperties: string[],
  entities: { [key: string]: APGLEntityEntry } = {},
) => {
  const matches = textProperties.some((propertyKey) => {
    return filterUsingOperator(item[propertyKey], value, ':', entities);
  });
  return matches;
};

const filterByToken = (
  token: any,
  item: any,
  filteringPropertiesMap: Map<string, readonly Operator[]>,
  textProperties: string[],
  entities: { [key: string]: APGLEntityEntry } = {},
) => {
  if (token.propertyKey) {
    if (
      !filteringPropertiesMap.has(token.propertyKey) ||
      // @ts-expect-error TS2532 - strict migration
      !filteringPropertiesMap.get(token.propertyKey).includes(token.operator)
    ) {
      return false;
    }
    const itemValue = fixupFalsyValues(item[token.propertyKey]);
    return filterUsingOperator(itemValue, token.value, token.operator);
  }
  return freeTextFilter(token.value, item, textProperties, entities);
};

// @ts-expect-error TS7006 - strict migration
const fixupFalsyValues = (value) => {
  if (typeof value === 'boolean') {
    return value + '';
  }
  if (value || value === 0) {
    return value;
  }
  return '';
};
